import { ActionTree } from 'vuex';
import ApiUrl from '../../../../api/url';
import { NodeOsAccountState } from './types';
import { RootState } from '../../../types';
import * as types from './mutation-types';
import { AppContext } from '../../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../../translator';
import { isHappenedOtherPartyError } from '../../../error-type';
import router from '../../../../router';
import ScreenUrl from '../../../../router/url';

export const actions: ActionTree<NodeOsAccountState, RootState> = {
    /**
     * ノード紐付きOSアカウント一覧取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id ノードID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { id, pageNumber, perPage, sort } = {}) {
        const requestParams = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        const nodeId = id;
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.Node.OsAccount.SEARCH.replace(':id', nodeId.toString()),
                    params: requestParams,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * OSアカウントのノード内検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 検索対象のノードID
     * @param {any} user 検索アカウント名
     * @returns {Promise<void>}
     */
    async directSearch({ dispatch, commit }, { id, user } = {}) {
        try {
            commit(types.DIRECT_SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.Node.Account.SEARCH.replace(':id', id.toString()),
                    params: { user },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DIRECT_SEARCH_SUCCESS, { os_accounts: resData });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.DIRECT_SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * OSアカウント追加
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} node_id ノードID
     * @param {os_account} os_account 追加OSアカウント
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, { node_id, os_account }) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.OsAccount.CREATE.replace(':id', node_id.toString()),
                    params: os_account,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.OsAccount.CREATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * OSアカウントの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} node_id ノードID
     * @param {any} account_id OSアカウントID
     */
    async show({ dispatch, commit }, { node_id, account_id }) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.Node.OsAccount.SHOW.replace(':id', node_id.toString()).replace(
                        ':account_id',
                        account_id.toString()
                    ),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * OSアカウント更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} osAccount 更新するOSアカウント情報
     * @param {any} node_id ノードID
     * @param {any} account_id OSアカウントID
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, { osAccount, node_id, account_id } = {}) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.Node.OsAccount.UPDATE.replace(':id', node_id.toString()).replace(
                        ':account_id',
                        account_id.toString()
                    ),
                    params: osAccount,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功時トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.OsAccount.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * OSアカウント除外
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} node_id ノードID
     * @param {any} account_id 除外するOSアカウントID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, { node_id, account_id }) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.Node.OsAccount.DESTROY.replace(':id', node_id.toString()),
                    params: { account_id: account_id },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.OsAccount.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * OSアカウントの接続テスト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} osAccount テストするOSアカウント情報
     * @param {any} node_id ノードID
     * @returns {Promise<void>}
     */
    async connection({ dispatch, commit, rootState }, { osAccount, nodeId }) {
        try {
            commit(types.CONNECTION_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.OsAccount.CONNECTION.replace(':id', nodeId.toString()),
                    params: osAccount,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CONNECTION_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.OsAccount.CONNECTED);
                dispatch('toastMessage', `${osAccount.account_name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CONNECTION_FAILURE, params);
        }
    },

    /**
     * 検索済みデータをクリアする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearWork({ commit }) {
        commit(types.CLEAR_WORK);
    },

    /**
     * エラーをクリアする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async resetError({ commit }) {
        commit(types.NEW_ACCOUNTS);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
